import React, { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faHeart, faShare, faShoppingCart, faCheck, faTag, faBox, faShieldAlt, faExclamationTriangle } from '@fortawesome/free-solid-svg-icons';
import { FiChevronLeft, FiChevronRight, FiShoppingBag } from 'react-icons/fi';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { Product } from '../components/ProductCard';
import { getPublicProductDetail, getActiveProducts } from '../api/productApi';
import { useCart } from '../contexts/CartContext';
import { DbProduct } from '../api/db';

interface ProductDetailProps {}

// Extended Product interface to include additional details from API
interface DetailedProduct extends Product {
  ogBox?: boolean;
  images?: string[];
  colorway?: string;
  sizeType?: string;
  type?: string;
  description?: string;
}

const ProductDetail: React.FC<ProductDetailProps> = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [product, setProduct] = useState<DetailedProduct | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedSize, setSelectedSize] = useState<string | null>(null);
  const [selectedImage, setSelectedImage] = useState<number>(0);
  const [relatedProducts, setRelatedProducts] = useState<Product[]>([]);
  const [inventoryError, setInventoryError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const { addToCart } = useCart();
  
  // Force scroll to top on component mount and when product ID changes
  useEffect(() => {
    // Immediately scroll to top with no animation to ensure the page starts at the top
    window.scrollTo(0, 0);
  }, [id]);
  
  // Fetch product data
  useEffect(() => {
    const fetchProduct = async () => {
      if (!id) return;
      
      try {
        setLoading(true);
        
        // Fetch the actual product data
        const productData = await getPublicProductDetail(id);
        console.log('Product data:', productData);
        
        // Format product for display
        let formattedProduct: DetailedProduct;
        
        // Check if we got simple product or detailed product response
        if (productData.name) {
          // This is already in frontend format
          formattedProduct = {
            ...productData,
            // Ensure we have images array
            images: productData.images || [productData.image],
            // Make sure we have og_box
            ogBox: productData.ogBox
          };
        } else {
          // This is raw admin API data that needs formatting
          let name = '';
          
          // Use array join for consistent hyphenation
          const nameParts = [productData.brand, productData.type];
          if (productData.collab && productData.collab.trim() !== '') {
            nameParts.push(productData.collab);
          }
          nameParts.push(productData.colorway);
          
          name = nameParts.join(' - ');
          
          // Format size display
          let sizeDisplay = productData.size || '';
          if (productData.size_type) {
            const type = productData.size_type;
            if (type === 'M' || type === 'W') {
              sizeDisplay = `${productData.size}${type}`;
            } else if (type === 'TD' || type === 'GS' || type.includes('Y') || type.includes('C')) {
              sizeDisplay = `${productData.size} (${type})`;
            } else {
              // Fallback if size type is something else
              sizeDisplay = `${productData.size} (${type})`;
            }
          }
          
          formattedProduct = {
            id: productData.id,
            name: name.trim(),
            brand: productData.brand,
            price: parseFloat(productData.price),
            image: productData.images?.[0] || '',
            images: productData.images || [],
            sizes: [productData.size], // Convert to array for compatibility
            sizeType: productData.size_type,
            size: productData.size,
            sizeDisplay: sizeDisplay,
            colorway: productData.colorway,
            condition: productData.condition,
            collab: productData.collab,
            type: productData.type,
            ogBox: productData.og_box,
            description: productData.description
          };
        }
        
        setProduct(formattedProduct);
        setSelectedSize(formattedProduct.size || formattedProduct.sizes?.[0] || null);
        
        // Fetch related products
        try {
          const allProducts = await getActiveProducts();
          // Filter out current product and get top 3-4 related products
          const related = allProducts
            .filter(p => p.id !== id)
            .filter(p => p.brand === formattedProduct.brand || p.colorway === formattedProduct.colorway)
            .slice(0, 4);
          
          setRelatedProducts(related);
        } catch (relatedError) {
          console.error('Error fetching related products:', relatedError);
          // Non-critical, so don't show error to user
        }
        
        setLoading(false);
      } catch (err) {
        console.error("Error fetching product:", err);
        setError("Failed to load product details. Please try again.");
        setLoading(false);
      }
    };
    
    fetchProduct();
  }, [id]);
  
  // Navigate to previous image
  const prevImage = () => {
    if (!product?.images || product.images.length === 0) return;
    setSelectedImage((prev) => (prev === 0 ? product.images!.length - 1 : prev - 1));
  };
  
  // Navigate to next image
  const nextImage = () => {
    if (!product?.images || product.images.length === 0) return;
    setSelectedImage((prev) => (prev === product.images!.length - 1 ? 0 : prev + 1));
  };
  
  // Handle size selection
  const handleSizeSelect = (size: string) => {
    setSelectedSize(size);
  };
  
  // Handle add to cart
  const handleAddToCart = () => {
    if (!selectedSize || !product) {
      alert("Please select a size");
      return;
    }
    
    setInventoryError(null); // Clear any previous errors
    setSuccessMessage(null); // Clear any previous success message
    
    // Convert to DbProduct format for cart
    const dbProduct: DbProduct = {
      id: String(product.id), // Convert id to string explicitly
      brand: product.brand,
      type: product.type || product.name.replace(product.brand, '').trim(),
      colorway: product.colorway || '',
      size: selectedSize,
      size_type: product.sizeType || '',
      condition: product.condition || 'New',
      price: product.price,
      og_box: product.ogBox || false,
      collab: product.collab,
      description: product.description,
      created_at: new Date(),
      status: 'active',
      availableQuantity: product.availableQuantity || 1 // Add available quantity
    };
    
    // Get the current image URL
    const imageUrl = product.images && product.images.length > 0 
      ? product.images[selectedImage] 
      : product.image;
    
    // Add to cart
    const result = addToCart(dbProduct, 1, imageUrl);
    
    // Show success feedback only if successfully added
    if (result.success) {
      // Show styled success message
      setSuccessMessage("Item successfully added to your cart");
      setTimeout(() => {
        setSuccessMessage(null);
      }, 3000);
    } else if (result.message) {
      // Show error message
      setInventoryError(result.message);
      setTimeout(() => {
        setInventoryError(null);
      }, 3000);
    }
  };

  return (
    <div className="min-h-screen bg-background-dark text-primary">
      <Header />
      
      <main className="py-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative">
        {/* Breadcrumb */}
        <nav className="mb-8">
          <ol className="flex items-center space-x-2 text-sm text-secondary">
            <li>
              <Link to="/" className="hover:text-accent transition-colors">Home</Link>
            </li>
            <li className="flex items-center">
              <span className="mx-2">/</span>
              <Link to="/#products" className="hover:text-accent transition-colors">Products</Link>
            </li>
            <li className="flex items-center">
              <span className="mx-2">/</span>
              <span className="text-primary">{product?.name || 'Product Details'}</span>
            </li>
          </ol>
        </nav>
        
        {/* Loading state */}
        {loading && (
          <div className="flex items-center justify-center py-32">
            <div className="animate-spin rounded-full h-16 w-16 border-t-2 border-b-2 border-accent"></div>
            <span className="ml-4 text-xl">Loading product details...</span>
          </div>
        )}
        
        {/* Error state */}
        {error && (
          <div className="bg-red-900/20 rounded-lg p-10 text-center my-16">
            <div className="text-red-400 mb-4">{error}</div>
            <button
              onClick={() => navigate(-1)}
              className="px-6 py-2 bg-background-light hover:bg-accent text-white rounded-md transition-colors"
            >
              Go Back
            </button>
          </div>
        )}
        
        {/* Product details */}
        {!loading && !error && product && (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-5 lg:gap-10 max-w-5xl mx-auto">
            {/* Add global notifications at the top */}
            {(successMessage || inventoryError) && (
              <div className="col-span-1 lg:col-span-2 mb-4">
                {successMessage && (
                  <div className="bg-green-900/50 border border-green-500 p-3 rounded-lg transition-all duration-300">
                    <div className="flex items-center">
                      <FontAwesomeIcon icon={faCheck} className="text-green-300 mr-2" />
                      <p className="text-green-300 text-sm">{successMessage}</p>
                    </div>
                  </div>
                )}
                
                {inventoryError && (
                  <div className="bg-red-900/50 border border-red-500 p-3 rounded-lg transition-all duration-300">
                    <div className="flex items-center">
                      <FontAwesomeIcon icon={faExclamationTriangle} className="text-red-300 mr-2" />
                      <p className="text-red-300 text-sm">{inventoryError}</p>
                    </div>
                  </div>
                )}
              </div>
            )}
            
            {/* Product images section */}
            <div className="space-y-3 mb-4 lg:mb-0">
              {/* Main image */}
              <div className="bg-background-light rounded-xl overflow-hidden relative group aspect-square">
                <img 
                  src={product.images && product.images.length > 0 
                    ? product.images[selectedImage] 
                    : product.image}
                  alt={product.name}
                  className="w-full h-full object-contain hover:scale-102 transition-transform duration-500"
                />
                
                {/* Navigation arrows - improved for mobile */}
                {product.images && product.images.length > 1 && (
                  <>
                    <button 
                      onClick={prevImage}
                      className="absolute left-2 top-1/2 transform -translate-y-1/2 w-8 h-8 rounded-full bg-black/60 flex items-center justify-center text-white opacity-70 hover:opacity-100 transition-opacity"
                      aria-label="Previous image"
                    >
                      <FiChevronLeft className="w-4 h-4" />
                    </button>
                    <button 
                      onClick={nextImage}
                      className="absolute right-2 top-1/2 transform -translate-y-1/2 w-8 h-8 rounded-full bg-black/60 flex items-center justify-center text-white opacity-70 hover:opacity-100 transition-opacity"
                      aria-label="Next image"
                    >
                      <FiChevronRight className="w-4 h-4" />
                    </button>
                  </>
                )}
              </div>
              
              {/* Thumbnail gallery - scrollable on mobile */}
              {product.images && product.images.length > 1 && (
                <div className="flex space-x-2 overflow-x-auto pb-2 snap-x scrollbar-none">
                  {product.images.map((img, index) => (
                    <button
                      key={index}
                      onClick={() => setSelectedImage(index)}
                      className={`flex-shrink-0 w-14 h-14 rounded-md overflow-hidden snap-start ${
                        selectedImage === index ? 'ring-2 ring-accent' : 'ring-1 ring-background-light'
                      }`}
                    >
                      <img 
                        src={img} 
                        alt={`Thumbnail ${index + 1}`} 
                        className="w-full h-full object-contain"
                      />
                    </button>
                  ))}
                </div>
              )}
            </div>
            
            {/* Product info section */}
            <div className="space-y-4">
              {/* Product title and price */}
              <div>
                <h1 className="text-lg sm:text-xl md:text-2xl font-bold text-primary">{product.name}</h1>
                <div className="mt-2">
                  <span className="text-lg sm:text-xl font-semibold text-accent">${product.price.toLocaleString()}</span>
                </div>
              </div>
              
              {/* Product description */}
              {product.description && (
                <div className="bg-background-light/30 rounded-xl p-4 sm:p-5">
                  <h3 className="text-base sm:text-lg font-semibold text-primary mb-3 flex items-center gap-2">
                    <div className="w-1 h-5 bg-accent rounded-full"></div>
                    Description
                  </h3>
                  <p className="text-gray-300 text-sm sm:text-base leading-relaxed">{product.description}</p>
                </div>
              )}
              
              {/* Product details */}
              <div className="bg-background-light/30 rounded-xl p-4 sm:p-5">
                <h3 className="text-base sm:text-lg font-semibold text-primary mb-4 flex items-center gap-2">
                  <div className="w-1 h-5 bg-accent rounded-full"></div>
                  Product Details
                </h3>
                
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <div className="flex justify-between items-center py-2 px-3 bg-background-light/50 rounded-lg">
                    <span className="text-gray-400 font-medium text-sm">Brand</span>
                    <span className="text-primary font-semibold">{product.brand}</span>
                  </div>
                  
                  <div className="flex justify-between items-center py-2 px-3 bg-background-light/50 rounded-lg">
                    <span className="text-gray-400 font-medium text-sm">Type</span>
                    <span className="text-primary font-semibold">{product.type}</span>
                  </div>
                  
                  <div className="flex justify-between items-center py-2 px-3 bg-background-light/50 rounded-lg">
                    <span className="text-gray-400 font-medium text-sm">Colorway</span>
                    <span className="text-primary font-semibold">{product.colorway}</span>
                  </div>
                  
                  <div className="flex justify-between items-center py-2 px-3 bg-background-light/50 rounded-lg">
                    <span className="text-gray-400 font-medium text-sm">Condition</span>
                    <span className="text-primary font-semibold">{product.condition}</span>
                  </div>
                  
                  {product.collab && (
                    <div className="flex justify-between items-center py-2 px-3 bg-background-light/50 rounded-lg">
                      <span className="text-gray-400 font-medium text-sm">Collaboration</span>
                      <span className="text-primary font-semibold">{product.collab}</span>
                    </div>
                  )}
                  
                  <div className="flex justify-between items-center py-2 px-3 bg-background-light/50 rounded-lg">
                    <span className="text-gray-400 font-medium text-sm">Original Box</span>
                    <span className={`font-semibold ${product.ogBox ? 'text-green-400' : 'text-orange-400'}`}>
                      {product.ogBox ? 'Yes' : 'No'}
                    </span>
                  </div>
                </div>
              </div>
              
              {/* Special features and highlights */}
              <div className="space-y-3">
                <h3 className="text-base font-semibold">Features</h3>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <div className="flex items-center gap-3 bg-background-light/50 p-3 rounded-lg">
                    <FontAwesomeIcon icon={faShieldAlt} className="text-accent h-4 w-4 sm:h-5 sm:w-5" />
                    <div>
                      <p className="font-medium text-sm sm:text-base">100% Authenticity Guaranteed</p>
                    </div>
                  </div>
                  
                  <div className="flex items-center gap-3 bg-background-light/50 p-3 rounded-lg">
                    <FontAwesomeIcon icon={faBox} className="text-accent h-4 w-4 sm:h-5 sm:w-5" />
                    <div>
                      <p className="font-medium text-sm sm:text-base">Original Box</p>
                      <p className="text-xs text-gray-400">{product.ogBox ? 'Included' : 'Not included'}</p>
                    </div>
                  </div>
                </div>
              </div>
              
              {/* Size selection - improved for mobile touch */}
              <div>
                <h3 className="text-base font-semibold mb-2">Size</h3>
                <div className="inline-flex items-center px-3 py-2 border border-background-light rounded-lg">
                  <span className="text-primary font-medium text-sm sm:text-base">{product.sizeDisplay || product.size}</span>
                </div>
              </div>
              
              {/* Call-to-action buttons */}
              <div className="flex flex-col sm:flex-row gap-3 pt-3">
                <button
                  onClick={handleAddToCart}
                  className="flex-1 bg-accent hover:bg-accent-dark text-white rounded-lg py-3 px-4 sm:px-5 font-medium flex items-center justify-center gap-2 transition-all duration-300 active:scale-95 touch-action-manipulation"
                >
                  <FontAwesomeIcon icon={faShoppingCart} className="h-4 w-4" />
                  <span>Add to Cart</span>
                </button>
                
                <button
                  className="flex-1 border border-accent/60 text-accent hover:bg-accent/10 rounded-lg py-3 px-4 sm:px-5 font-medium flex items-center justify-center gap-2 transition-colors duration-300 active:scale-95 touch-action-manipulation"
                >
                  <FiShoppingBag className="h-4 w-4" />
                  <span>Buy Now</span>
                </button>
              </div>
            </div>
          </div>
        )}
        
        {/* Related products section */}
        {!loading && !error && relatedProducts.length > 0 && (
          <div className="mt-12 sm:mt-16">
            <div className="flex items-center justify-between mb-4 sm:mb-6">
              <h2 className="text-lg sm:text-xl font-bold">You Might Also Like</h2>
              <Link to="/all-heat" className="text-accent hover:text-accent-light text-xs sm:text-sm font-medium transition-colors">
                View All
              </Link>
            </div>
            
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 sm:gap-6">
              {relatedProducts.map(product => (
                <div key={product.id} className="col-span-1">
                  {/* Use your ProductCard component here */}
                  <div className="aspect-square rounded-lg bg-background-light overflow-hidden">
                    <Link to={`/product/${product.id}`}>
                      <img 
                        src={product.image} 
                        alt={product.name}
                        className="w-full h-full object-cover hover:scale-105 transition-transform duration-500"
                      />
                    </Link>
                  </div>
                  <div className="mt-2">
                    <Link to={`/product/${product.id}`} className="text-sm font-medium hover:text-accent transition-colors line-clamp-1">
                      {product.name}
                    </Link>
                    <p className="text-accent font-bold">${product.price}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </main>
      <Footer />
    </div>
  );
};

export default ProductDetail; 